import React from 'react';
import { motion } from 'framer-motion';
import { CreditCardIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { createPortalSession } from '../api/stripe';

function SubscriptionManager({ plan, onError }) {
  const { user } = useAuth();
  const [loading, setLoading] = React.useState(false);

  const handleManageSubscription = async () => {
    try {
      setLoading(true);
      await createPortalSession(user.id);
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      onError?.(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getPlanDetails = () => {
    switch (plan?.toLowerCase()) {
      case 'pro':
        return {
          name: 'Pro Plan',
          features: ['5,000 API calls/month', 'Priority support', 'API access']
        };
      case 'business':
        return {
          name: 'Business Plan',
          features: ['25,000 API calls/month', 'Priority support', 'Advanced analytics']
        };
      case 'enterprise':
        return {
          name: 'Enterprise Plan',
          features: ['Custom API limits', '24/7 support', 'Dedicated manager']
        };
      default:
        return {
          name: 'Free Plan',
          features: ['Limited access', 'Community support']
        };
    }
  };

  const planDetails = getPlanDetails();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass p-6 rounded-modern"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-white">Subscription</h3>
        <CreditCardIcon className="w-6 h-6 text-blue-400" />
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-white/80">Current Plan</span>
          <span className="text-white font-medium">{planDetails.name}</span>
        </div>
        <div className="space-y-2">
          {planDetails.features.map((feature, index) => (
            <div key={index} className="text-sm text-white/60 flex items-center">
              <ChevronRightIcon className="w-4 h-4 mr-2 text-blue-400" />
              {feature}
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={handleManageSubscription}
        disabled={loading}
        className="w-full py-2 px-4 bg-gradient-to-br from-[#2563EB] to-[#4F46E5] text-white rounded-modern font-medium hover:opacity-90 transition-opacity duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {loading ? (
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        ) : (
          'Manage Subscription'
        )}
      </button>
    </motion.div>
  );
}

export default SubscriptionManager;
